// Pure measurement utility functions shared by PeitepaksuusBlock, PoranayteBlock, etc.
// Kept side-effect-free so they can be unit-tested without React or API mocks.

export const PEITEPAKSUUS_RANGES = [
  { label: '< 6',   test: v => v < 6 },
  { label: '6–10',  test: v => v >= 6  && v <= 10 },
  { label: '11–15', test: v => v >= 11 && v <= 15 },
  { label: '16–20', test: v => v >= 16 && v <= 20 },
  { label: '21–25', test: v => v >= 21 && v <= 25 },
  { label: '26–30', test: v => v >= 26 && v <= 30 },
  { label: '31–35', test: v => v >= 31 && v <= 35 },
  { label: '36–40', test: v => v >= 36 && v <= 40 },
  { label: '41–45', test: v => v >= 41 && v <= 45 },
  { label: '> 45',  test: v => v > 45 },
];

export function parseRows(rows) {
  return rows.map(r => parseFloat(String(r).replace(',', '.'))).filter(n => !isNaN(n) && n > 0);
}

export function calcStats(arr) {
  if (!arr.length) return { ka: null, min: null, max: null, khaj: null };
  const min = Math.min(...arr);
  const max = Math.max(...arr);
  const ka = arr.reduce((a, b) => a + b, 0) / arr.length;
  const khaj = arr.length > 1
    ? Math.sqrt(arr.reduce((s, v) => s + (v - ka) ** 2, 0) / (arr.length - 1))
    : 0;
  return { ka, min, max, khaj };
}

export function calcDist(arr, ranges = PEITEPAKSUUS_RANGES) {
  const total = arr.length;
  return ranges.map(r => {
    const count = arr.filter(r.test).length;
    return { count, pct: total ? count / total : 0 };
  });
}

export function initCombinedCarbRows(raw) {
  if (!raw) return [''];
  const src = Array.isArray(raw) ? raw : [...(raw.alapinta ?? []), ...(raw.ylapinta ?? [])];
  const vals = src.map(String).filter(s => s.trim() !== '' && s !== '0');
  return vals.length ? [...vals, ''] : [''];
}

// ---------------------------------------------------------------------------
// Karb surface defaults per rakenneosa
// ---------------------------------------------------------------------------

const KARB_RAKENNEOSA_DEFAULTS = [
  { match: 'parvekelaatta',      upper: 'yläpinta',  lower: 'alapinta',  karb_to_table: 'lower' },
  { match: 'parvekekaide',       upper: 'ulkopinta', lower: 'sisäpinta', karb_to_table: 'upper' },
  { match: 'parvekepieli',       upper: 'ulkopinta', lower: 'sisäpinta', karb_to_table: 'upper' },
  { match: 'julkisivuelementti', upper: 'ulkopinta', lower: 'sisäpinta', karb_to_table: 'upper' },
  { match: 'sokkeli',            upper: 'ulkopinta', lower: 'sisäpinta', karb_to_table: 'upper' },
  { match: 'pilari',             upper: 'ulkopinta', lower: 'sisäpinta', karb_to_table: 'upper' },
  { match: 'palkki',             upper: 'ulkopinta', lower: 'sisäpinta', karb_to_table: 'upper' },
];

export function karbDefaultsForRakenneosa(rakenneosa = '') {
  const lower = rakenneosa.toLowerCase();
  const match = KARB_RAKENNEOSA_DEFAULTS.find(r => lower.includes(r.match));
  return match
    ? { karb_upper_pinta: match.upper, karb_lower_pinta: match.lower, karb_to_table: match.karb_to_table }
    : { karb_upper_pinta: 'ulkopinta', karb_lower_pinta: 'sisäpinta', karb_to_table: 'upper' };
}

// ---------------------------------------------------------------------------
// Karb-per-näyte sync logic (pure core, used by upsertKarbPerNayte)
// ---------------------------------------------------------------------------

/**
 * Given a single nayte's karb data object and the rakenneosa-level default,
 * returns which km value should flow to the peitepaksuus carbonation table.
 */
export function resolveKarbKm(nayteData, defaultToTable) {
  const toTable = nayteData.to_table ?? defaultToTable;
  return toTable === 'lower' ? nayteData.lower_km ?? null : nayteData.upper_km ?? null;
}

/**
 * Builds the carbonation_measurements payload and label array from karb_per_nayte,
 * respecting the rakenneosa surface default when to_table is not yet explicitly set.
 */
export function buildCarbonationPayload(karbPerNayte, rakenneosa = '') {
  const defaultToTable = karbDefaultsForRakenneosa(rakenneosa).karb_to_table;
  const entries = Object.entries(karbPerNayte)
    .map(([t, d]) => ({ t, km: resolveKarbKm(d, defaultToTable) }))
    .filter(e => e.km != null);
  return {
    carbonation_measurements: { alapinta: entries.map(e => e.km), ylapinta: [] },
    karb_tunnus_labels: entries.map(e => e.t),
  };
}

// ---------------------------------------------------------------------------
// syncPoranayteRow deduplication guard
// ---------------------------------------------------------------------------

/**
 * Returns true when a new peitepaksuus kohde should be created for this nayte.
 * One kohde per rakenneosa name — returns false if a kohde with the same name
 * or the same nayte_id already exists.
 */
export function shouldCreatePeitepaksuusKohde(kohteet, nayteId, rakenneosa, nayteTunnus) {
  const list = kohteet ?? [];
  if (list.find(k => k.nayte_id === nayteId)) return false;
  if (list.some(k => k.nimi === (rakenneosa || nayteTunnus))) return false;
  return true;
}
